import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE, apiUrl } from "../../../config/api";
import { isWeightedUnit, normalizeSaleUnit, roundedLineTotal } from "./saleUnits";

// One key per sale attempt. The server records at most one sale per key, so a
// double click or a retry after a lost response replays the original sale.
export function createSaleOperationKey() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi?.randomUUID) return `pos-${cryptoApi.randomUUID()}`;
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  const random = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `pos-${Date.now().toString(36)}-${random}`;
}

export default function usePOSLogic() {
  // ---------- Refs ----------
  const searchContainerRef = useRef(null);
  const locationDropdownRef = useRef(null);

  // ---------- Basic UI / Cart ----------
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [qtyInputs, setQtyInputs] = useState({});

  // ---------- Modals & Alerts ----------
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCartEmptyAlert, setShowCartEmptyAlert] = useState(false);
  const [showGenericModal, setShowGenericModal] = useState(false);
  const [genericModalContent, setGenericModalContent] = useState({ title: "", message: "", onClose: null });
  const [showLocationChangeModal, setShowLocationChangeModal] = useState(false);
  const [pendingLocationChange, setPendingLocationChange] = useState(null);

  // ---------- Worker / Discounts ----------
  const [workerCode, setWorkerCode] = useState("");
  const [workerName, setWorkerName] = useState("");
  const [workerCodeError, setWorkerCodeError] = useState("");
  const [clientUsername, setClientUsername] = useState("");
  const [workerDiscount, setWorkerDiscount] = useState(null);
  const [workerVerificationToken, setWorkerVerificationToken] = useState("");

  // ---------- Payments ----------
  const [cashAmount, setCashAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [cashGiven, setCashGiven] = useState("");
  const [cardGiven, setCardGiven] = useState("");
  const [confirmedCardTotal, setConfirmedCardTotal] = useState(0);
  const [noTicket, setNoTicket] = useState(false);

  // Manual registration (no machine): allow confirming if cash + typed card + confirmed card >= total
  const [manualWithoutMachine, setManualWithoutMachine] = useState(false);

  // Track the in-flight MP order
  const [, setCurrentOrderId] = useState(null);

  // Sale registration: in-flight lock + operation key kept until the outcome is known
  const [submittingSale, setSubmittingSale] = useState(false);
  const saleInFlightRef = useRef(false);
  const saleOperationKeyRef = useRef(null);
  const terminalAttemptRef = useRef(null);
  const appliedTerminalKeysRef = useRef(new Set());
  const terminalSendingRef = useRef(false);

  // ---------- Locations ----------
  const availableLocations = useMemo(
    () => JSON.parse(localStorage.getItem("availableLocations") || "{}"),
    []
  );
  const locationKeys = useMemo(() => Object.keys(availableLocations), [availableLocations]);
  const isSingleLocation = useMemo(() => locationKeys.length === 1, [locationKeys]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false); // deprecated: no dropdown in entry
  const [showLocationSelect, setShowLocationSelect] = useState(false);

  // ---------- Custom Product ----------
  const [showCustomProductModal, setShowCustomProductModal] = useState(false);
  const [customProduct, setCustomProduct] = useState({ description: "", unit_price: "", reqQty: 1 });

  // ---------- Payment Machines ----------
  const [paymentMachines, setPaymentMachines] = useState([]); // [{id, name, provider, serial, location_id}, ...]
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [machinesError, setMachinesError] = useState("");
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [sendingPayment, setSendingPayment] = useState(false);

  // ---------- Derived ----------
  const discountedTotal = useMemo(
    () => (workerDiscount ? Math.round(total * (1 - workerDiscount / 100)) : Math.round(total)),
    [total, workerDiscount]
  );
  const totalEntered = cashAmount + cardAmount; // only textboxes
  const remaining = discountedTotal - (confirmedCardTotal + totalEntered); // ⬅️ include confirmed card  
  const posSidesWidth = "350px";

  // In manual mode, we allow confirmation if cash + typed card + confirmed card >= total
  // In machine mode, only cash + confirmed card count
  const canConfirm = useMemo(() => {
    if (manualWithoutMachine) {
      return (Number(cashAmount || 0) + Number(cardAmount || 0) + Number(confirmedCardTotal || 0)) >= Number(discountedTotal || 0);
    }
    return (Number(cashAmount || 0) + Number(confirmedCardTotal || 0)) >= Number(discountedTotal || 0);
  }, [manualWithoutMachine, cashAmount, cardAmount, confirmedCardTotal, discountedTotal]);

  // ---------- Effects ----------
  useEffect(() => {
    const newTotal = cart.reduce((sum, item) => {
      return sum + roundedLineTotal(item.unit_price, item.reqQty, item.sale_unit);
    }, 0);
    setTotal(newTotal);
  }, [cart]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchQuery("");
        setSearchResults([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (workerCode.trim().length > 0) {
        verifyWorkerCode(workerCode);
      } else {
        setWorkerName("");
        setWorkerDiscount(null);
        setClientUsername("");
        setWorkerVerificationToken("");
        setWorkerCodeError("");
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [workerCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial location selection/activation on POS entry
  const [checkedActive, setCheckedActive] = useState(false);
  const [locationOptions, setLocationOptions] = useState(null); // [{id,name, available_for_me, active_user, status}]
  const [locationAvailability, setLocationAvailability] = useState(null);
  const [noAvailableLocations, setNoAvailableLocations] = useState(false);
  useEffect(() => {
    async function run() {
      if (checkedActive) return;
      // 1) If user already holds an active POS, jump straight in
      try {
        const sessionData = JSON.parse(localStorage.getItem("sessionData"));
        if (sessionData?.sessionKey) {
          const res = await fetch(`${API_BASE}/locations/active`, {
            method: 'GET',
            headers: { Authorization: sessionData.sessionKey, Username: sessionData.username },
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.location?.id) {
              setSelectedLocation(String(data.location.id));
              setShowLocationSelect(false);
              setPendingActivation(null);
              setCheckedActive(true);
              return;
            }
          }
        }
      } catch (e) {
        // ignore and continue to normal flow
      }

      // 2) Normal flow: ask or auto-activate based on count
      if (!selectedLocation) {
        if (isSingleLocation && locationKeys.length === 1) {
          void selectAndActivateLocation(locationKeys[0]);
        } else if (locationKeys.length > 1) {
          // Filter to available ones
          try {
            const res = await fetch(`${API_BASE}/locations/availability?ids=${encodeURIComponent(locationKeys.join(','))}`, {
              method: 'GET',
              headers: { Authorization: JSON.parse(localStorage.getItem('sessionData')).sessionKey, Username: JSON.parse(localStorage.getItem('sessionData')).username },
            });
            if (res.ok) {
              const data = await res.json();
              const list = (Array.isArray(data?.locations) ? data.locations : []).map(x => ({
                id: String(x.id), name: x.name, available_for_me: !!x.available_for_me,
                active_user: x.active_user, status: x.status
              }));
              setLocationAvailability(list);
              const avail = list.filter(x => x.available_for_me).map(x => x.id);
              if (!avail.length) {
                setNoAvailableLocations(true);
              } else {
                // options for dropdown include all (both available and occupied); mark availability
                const opts = list.map(o => ({ id: o.id, name: availableLocations[o.id] || o.name, available_for_me: o.available_for_me, active_user: o.active_user, status: o.status }));
                setLocationOptions(opts);
                setShowLocationSelect(true);
              }
            } else {
              // fallback: show all (without availability info)
              const opts = locationKeys.map(id => ({ id: String(id), name: availableLocations[id] }));
              setLocationOptions(opts);
              setShowLocationSelect(true);
            }
          } catch {
            const opts = locationKeys.map(id => ({ id: String(id), name: availableLocations[id] }));
            setLocationOptions(opts);
            setShowLocationSelect(true);
          }
        }
      }
      setCheckedActive(true);
    }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleLocation, locationKeys, selectedLocation, checkedActive]);

  const [pendingActivation, setPendingActivation] = useState(null);
  const [forceCloseTarget, setForceCloseTarget] = useState(null); // { id, expected_balance, active_user }

  async function selectAndActivateLocation(locId) {
    try {
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey) return;
      // Fetch state
      const stateRes = await fetch(`${API_BASE}/locations/${locId}/state`, {
        method: 'GET',
        headers: { Authorization: sessionData.sessionKey, Username: sessionData.username },
      });
      if (!stateRes.ok) {
        alert('No se pudo obtener el estado de la ubicación.');
        return;
      }
      const st = await stateRes.json();
      const loc = st.location || {};
      if (loc.active_user && loc.active_user !== sessionData.username && loc.status === 'active') {
        setShowLocationSelect(false);
        setForceCloseTarget({ id: String(locId), expected_balance: Number(loc.actual_balance || 0), active_user: loc.active_user });
        return;
      }
      // Open custom balance dialog via pendingActivation
      setPendingActivation({ id: String(locId), name: loc.name, actual_balance: Number(loc.actual_balance || 0) });
    } catch (e) {
      console.error('selectAndActivateLocation error', e);
      alert('Error al activar la caja.');
    }
  }

  async function activatePendingLocation({ confirmSame, physicalBalance }) {
    try {
      if (!pendingActivation) return;
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey) return;
      const body = confirmSame
        ? { location_id: Number(pendingActivation.id), confirm_same: true }
        : { location_id: Number(pendingActivation.id), confirm_same: false, physical_balance: Number(physicalBalance) };
      const actRes = await fetch(`${API_BASE}/locations/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username },
        body: JSON.stringify(body),
      });
      if (!actRes.ok) {
        const t = await actRes.text();
        alert(`No se pudo activar la caja. ${t}`);
        return;
      }
      setSelectedLocation(String(pendingActivation.id));
      setShowLocationSelect(false);
      setPendingActivation(null);
    } catch (e) {
      console.error('activatePendingLocation error', e);
      alert('Error al confirmar el balance.');
    }
  }

  // barcode / keyboard buffer search
  useEffect(() => {
    let buffer = "";
    function handleKeydown(e) {
      const tag = document.activeElement.tagName;
      const isInputFocused = ["INPUT", "TEXTAREA"].includes(tag);
      const isTabActive = document.hasFocus();
      if (!isTabActive || isInputFocused) return;

      if (e.key === "Enter") {
        if (buffer.trim()) {
          // The debounced search effect below runs the lookup for the scanned
          // code with the current location (and auto-adds a single match).
          setSearchQuery(buffer);
          buffer = "";
        }
      } else if (e.key.length === 1) {
        buffer += e.key;
      }
    }
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, []);

  const showModal = useCallback((content) => {
    setGenericModalContent(content);
    setShowGenericModal(true);
  }, []);

  const addProductToCart = useCallback((product) => {
    if (!String(product?.code || "").trim()) {
      showModal({
        title: "Producto sin código",
        message: "Este producto necesita un código de barras antes de poder venderse en el POS.",
        onClose: () => setShowGenericModal(false),
      });
      return;
    }
    setCart((prevCart) => {
      const existingProduct = prevCart.find((item) => item.code === product.code);
      if (existingProduct) {
        if (isWeightedUnit(existingProduct.sale_unit)) return prevCart;
        const updatedCart = prevCart.map((item) =>
          item.code === product.code ? { ...item, reqQty: item.reqQty + 1 } : item
        );
        setQtyInputs((prev) => ({ ...prev, [product.code]: (existingProduct.reqQty + 1).toString() }));
        return updatedCart;
      } else {
        const weighted = isWeightedUnit(product.sale_unit);
        const initialQty = weighted ? "" : "1";
        setQtyInputs((prev) => ({ ...prev, [product.code]: initialQty }));
        return [...prevCart, { ...product, sale_unit: normalizeSaleUnit(product.sale_unit), reqQty: weighted ? 0 : 1 }];
      }
    });
  }, [showModal]);

  // debounced search when typing
  useEffect(() => {
    const delay = setTimeout(async () => {
      const trimmed = searchQuery.trim();
      if (!trimmed) return;

      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData || !sessionData.sessionKey || !selectedLocation) return;

      try {
        const locationIdsHeader = selectedLocation.toString();
        const response = await fetch(
          apiUrl(`/products/search?q=${encodeURIComponent(trimmed)}`),
          {
            method: "GET",
            headers: {
              Authorization: sessionData.sessionKey,
              username: sessionData.username,
              locations: locationIdsHeader,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const products = result.products || [];
          setSearchResults(products);
          if (products.length === 1) {
            // only add if modal is not open
            if (!showPaymentModal) {
              addProductToCart(products[0]);
              setSearchQuery("");
              setSearchResults([]);
            }
          }
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Error searching products:", err);
      }
    }, 200);
    return () => clearTimeout(delay);
  }, [searchQuery, selectedLocation, showPaymentModal, addProductToCart]);

  // ---------- Handlers / Logic ----------
  async function searchProduct(searchString) {
    try {
      const sanitizedSearchString = searchString.trim();
      if (!sanitizedSearchString) {
        setSearchResults([]);
        return;
      }
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!selectedLocation) {
        setSearchResults([]);
        return;
      }
      const locationIdsHeader = selectedLocation.toString();
      if (!sessionData || !sessionData.sessionKey) {
        console.error("Session key is missing");
        return;
      }

      const response = await fetch(
        apiUrl(`/products/search?q=${encodeURIComponent(sanitizedSearchString)}`),
        {
          method: "GET",
          headers: {
            Authorization: sessionData.sessionKey,
            username: sessionData.username,
            locations: locationIdsHeader,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSearchResults(result.products || []);
      } else if (response.status === 404) {
        setSearchResults([]);
      } else {
        console.error(`Failed to search products. Status: ${response.status}, Message: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error searching products:", error);
    }
  }

  function removeProductFromCart(productId) {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  }

  function incrementQty(code) {
    setCart((prevCart) => {
      const updatedCart = prevCart.map((item) => {
        const isUnlimited = String(item.code || "").startsWith("custom-") || isNaN(item.qty);
        const canAddMore = isUnlimited || item.reqQty < item.qty;
        if (item.code === code && canAddMore) {
          const newQty = item.reqQty + 1;
          setQtyInputs((prev) => ({ ...prev, [code]: newQty.toString() }));
          return { ...item, reqQty: newQty };
        }
        return item;
      });
      return updatedCart;
    });
  }

  function decrementQty(code) {
    setCart((prevCart) =>
      prevCart.flatMap((item) => {
        if (item.code !== code) return item;
        if (item.reqQty > 1) {
          const newQty = item.reqQty - 1;
          setQtyInputs((prev) => ({ ...prev, [code]: newQty.toString() }));
          return { ...item, reqQty: newQty };
        }
        setQtyInputs((prev) => {
          const copy = { ...prev };
          delete copy[code];
          return copy;
        });
        return [];
      })
    );
  }

  function handleQtyChange(e, code, item) {
    const raw = e.target.value;
    const normalized = raw.replace(",", ".");
    const unit = normalizeSaleUnit(item.sale_unit);
    const isKg = unit === "kg";
    const pattern = isKg ? /^\d*(?:[.,]\d{0,3})?$/ : /^\d*$/;
    if (!pattern.test(raw)) return;

    setQtyInputs((prev) => ({ ...prev, [code]: raw }));
    const value = Number(normalized);
    if (!raw || !Number.isFinite(value) || value <= 0) {
      setCart((prevCart) => prevCart.map((el) => (el.code === code ? { ...el, reqQty: 0 } : el)));
      return;
    }
    const unlimited = String(item.code || "").startsWith("custom-") || !Number.isFinite(Number(item.qty));
    const available = unlimited ? value : Number(item.qty);
    const cleanQty = Math.min(value, available);
    setCart((prevCart) => prevCart.map((el) => (el.code === code ? { ...el, reqQty: cleanQty } : el)));
  }

  async function verifyWorkerCode(code) {
    try {
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData || !sessionData.sessionKey || !sessionData.username) {
        setWorkerCodeError("Sesión inválida");
        return;
      }
      const response = await fetch(apiUrl("/sales/workers/verify-code"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
        },
        body: JSON.stringify({ code }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setWorkerName(result.name);
        setClientUsername(result.username);
        setWorkerDiscount(Number(result.discount || 0));
        setWorkerVerificationToken(result.verification_token || "");
        setWorkerCodeError("");
      } else {
        setWorkerName("");
        setWorkerDiscount(null);
        setClientUsername("");
        setWorkerVerificationToken("");
        setWorkerCodeError("Código inválido");
      }
    } catch (err) {
      console.error("Error verifying worker code:", err);
      setWorkerName("");
      setWorkerDiscount(null);
      setClientUsername("");
      setWorkerVerificationToken("");
      setWorkerCodeError("Error de conexión");
    }
  }

  function parseAmount(value) {
    const raw = value.replace(/[^\d]/g, "");
    return parseInt(raw || "0", 10);
  }

  function resetPaymentState() {
    setCashGiven("");
    setCashAmount(0);
    setCardGiven("");
    setCardAmount(0);
    setConfirmedCardTotal(0); // ⬅️ new
    setWorkerCode("");
    setWorkerName("");
    setWorkerCodeError("");
    setClientUsername("");
    setWorkerVerificationToken("");
    setNoTicket(false);
    setManualWithoutMachine(false);
    setCurrentOrderId(null);
  }  

  async function handleSubmitSale({ cardOverride = null } = {}) {
    // In-flight lock: a double click (or a machine approval racing a manual
    // confirm) must not send a second registration while one is pending.
    if (saleInFlightRef.current) return;

    const sessionData = JSON.parse(localStorage.getItem("sessionData"));
    if (!sessionData?.sessionKey || !sessionData?.username) {
      showModal({ title: "Error de sesión", message: "Sesión inválida. Inicia sesión nuevamente.", onClose: () => setShowGenericModal(false) });
      return;
    }

    const parsedCash = parseInt((cashGiven || "").replace(/\./g, ""), 10) || 0;

 // Only add typed card when we're in manual mode. In machine mode, rely on confirmed card or override.
    const typedCardForManual = manualWithoutMachine ? (Number(cardAmount || 0)) : 0;
    const cardToRecord =
      (cardOverride !== null && cardOverride !== undefined)
        ? Number(cardOverride)
        : Number(confirmedCardTotal || 0) + typedCardForManual;

    const change = parsedCash + cardToRecord - discountedTotal;
    const computedCashAmount = change >= 0
      ? Math.max(discountedTotal - cardToRecord, 0)
      : parsedCash;

    const locationId = parseInt(selectedLocation || "0", 10);
    const cardVoucherCode = document.getElementById("card-voucher")?.value || "";

    // Reuse the pending key until the server tells us how this sale ended, so
    // a retry after a lost answer replays the sale instead of duplicating it.
    if (!saleOperationKeyRef.current) saleOperationKeyRef.current = createSaleOperationKey();

    const payload = {
      cart: cart.map((item) => ({
        code: item.code,
        description: item.description,
        quantity: item.reqQty,
        unit_price: parseFloat(item.unit_price),
        sale_unit: normalizeSaleUnit(item.sale_unit || "un"),
      })),
      payments: {
        worker: 0,
        cash: computedCashAmount,
        cash_given: parsedCash,
        card: cardToRecord,
      },
      card_voucher_code: cardVoucherCode,
      client_username: clientUsername || "client",
      cashier_username: sessionData.username,
      timestamp: new Date().toISOString(),
      location: locationId,
      discount: workerDiscount || 0,
      worker_verification_token: workerVerificationToken || null,
      operation_key: saleOperationKeyRef.current,
    };

    saleInFlightRef.current = true;
    setSubmittingSale(true);
    try {
      const response = await fetch(apiUrl("/sales/register"), {
        method: "POST",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let result = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }

      if (response.ok && result?.success) {
        // Outcome known: the sale (and, for cash, the register balance, which
        // the server updates in the same transaction) is recorded exactly once.
        saleOperationKeyRef.current = null;
        terminalAttemptRef.current = null;
        appliedTerminalKeysRef.current.clear();
        try { localStorage.removeItem(`ovm_terminal_attempt_${sessionData.username}`); } catch { /* server receipt is durable */ }
        showModal({
          title: "Venta registrada",
          message: result.replayed
            ? `La venta #${result.saleId} ya estaba registrada; no se duplicó.`
            : "La venta ha sido registrada exitosamente.",
          onClose: () => setShowGenericModal(false),
        });
        setCart([]);
        setShowPaymentModal(false);
        resetPaymentState();
        return true;
      }

      if (result?.code === "SALE_OPERATION_CONFLICT") {
        // An earlier attempt of this operation was recorded with other details.
        saleOperationKeyRef.current = null;
        showModal({
          title: "Venta ya registrada",
          message: result.message || "Esta operación ya registró una venta. Revisa las ventas antes de volver a cobrar.",
          onClose: () => setShowGenericModal(false),
        });
        return;
      }

      if (!result || response.status >= 500) {
        // Unknown outcome (server/proxy failure or unreadable answer): keep the
        // key so pressing Confirmar again cannot record the sale twice.
        showModal({
          title: "Venta sin confirmar",
          message: "No se pudo confirmar si la venta quedó registrada. Presiona Confirmar nuevamente: si ya se registró, no se duplicará.",
          onClose: () => setShowGenericModal(false),
        });
        return;
      }

      showModal({
        title: "Error al registrar venta",
        message: `Error al registrar venta: ${result.message}`,
        onClose: () => setShowGenericModal(false),
      });
    } catch (error) {
      console.error("Error al registrar venta:", error);
      showModal({
        title: "Error de red",
        message: "No se pudo confirmar la venta por un error de red. Presiona Confirmar nuevamente: si ya se registró, no se duplicará.",
        onClose: () => setShowGenericModal(false),
      });
    } finally {
      saleInFlightRef.current = false;
      setSubmittingSale(false);
    }
  }

  // ---------- Payment Machines logic ----------
  const loadPaymentMachines = useCallback(async (locationId) => {
    setMachinesLoading(true);
    setMachinesError("");
    try {
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey || !sessionData?.username) {
        setMachinesError("Sesión inválida");
        setPaymentMachines([]);
        setSelectedMachineId(null);
        return;
      }
  
      const res = await fetch(apiUrl(`/sales/machines?location=${locationId}`), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });
  
      if (!res.ok) {
        setMachinesError(`No se pudieron cargar las máquinas (HTTP ${res.status})`);
        setPaymentMachines([]);
        setSelectedMachineId(null);
        return;
      }
  
      const data = await res.json();
      // rows: [{ terminal_id, location, auth_token }]
      const list = Array.isArray(data?.machines) ? data.machines : [];

      // 🔧 Normalize to { id, label, raw }
      const normalized = list.map((m) => ({
        id: String(m.internal_id), // use internal_id as stable unique ID
        label: `${m.internal_id}`,
        raw: m,
      }));

      setPaymentMachines(normalized);
      setSelectedMachineId(normalized.length ? normalized[0].id : null);

      const recoveryResponse = await fetch(apiUrl('/sales/machines/pending-charge'), {
        headers: { Authorization: sessionData.sessionKey, Username: sessionData.username },
      });
      const recoveryData = await recoveryResponse.json();
      if (!recoveryResponse.ok || !recoveryData.success) throw new Error(recoveryData.message || 'No se pudo consultar el cobro anterior.');
      const recovery = recoveryData.recovery;
      if (recovery && recovery.saleOperationKey !== saleOperationKeyRef.current) {
        showModal({ title: 'Recuperar venta pendiente', message: 'Hay una venta con un cobro enviado o un pago parcial. Recupérala antes de iniciar otra venta.',
          recover: () => {
            const snapshot = recovery.snapshot || {};
            if (!Array.isArray(snapshot.cart) || !snapshot.cart.length) return;
            setCart(snapshot.cart);
            setSelectedLocation(String(snapshot.selectedLocation));
            setSelectedMachineId(recovery.internalId);
            setCashGiven(snapshot.cashGiven || '');
            setCashAmount(Number(snapshot.cashAmount || 0));
            setWorkerDiscount(snapshot.workerDiscount ?? null);
            setWorkerVerificationToken(snapshot.workerVerificationToken || '');
            setClientUsername(snapshot.clientUsername || '');
            setConfirmedCardTotal(recovery.confirmedCardTotal || 0);
            const amount = recovery.operationKey ? recovery.amount : Math.max(Number(snapshot.discountedTotal || 0) - Number(snapshot.cashAmount || 0) - recovery.confirmedCardTotal, 0);
            setCardAmount(amount);
            setCardGiven(String(amount));
            setNoTicket(recovery.printOnTerminal === 'no_ticket');
            saleOperationKeyRef.current = recovery.saleOperationKey;
            appliedTerminalKeysRef.current.clear();
            terminalAttemptRef.current = recovery.operationKey ? { key: recovery.operationKey, fingerprint: JSON.stringify({ internal_id: recovery.internalId, amount: recovery.amount, print_on_terminal: recovery.printOnTerminal, default_type: recovery.defaultType }) } : null;
            try { localStorage.removeItem(`ovm_terminal_attempt_${sessionData.username}`); } catch { /* server remains authoritative */ }
            setShowGenericModal(false);
            setShowPaymentModal(true);
          }, onClose: () => setShowGenericModal(false),
        });
      }

    } catch (e) {
      console.error("Error loading payment machines:", e);
      setMachinesError("Error al cargar las máquinas");
      setPaymentMachines([]);
      setSelectedMachineId(null);
    } finally {
      setMachinesLoading(false);
    }
  }, [showModal]);

  useEffect(() => {
    if (selectedLocation) loadPaymentMachines(selectedLocation);
  }, [showPaymentModal, selectedLocation, loadPaymentMachines]);

  async function sendCardPaymentToMachine() {
    if (terminalSendingRef.current) return;
    // Use exactly what’s typed in the card textbox
    const amountToCharge = Number(cardAmount || 0);
  
    if (!selectedMachineId) {
      showModal({ title: "Máquina requerida", message: "Selecciona una máquina de pago.", onClose: () => setShowGenericModal(false) });
      return;
    }
    if (!amountToCharge || amountToCharge <= 0) {
      showModal({ title: "Monto inválido", message: "Ingresa un monto válido en la casilla de tarjeta.", onClose: () => setShowGenericModal(false) });
      return;
    }
  
    terminalSendingRef.current = true;
    try {
      setSendingPayment(true);
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey || !sessionData?.username) {
        showModal({ title: "Error de sesión", message: "Sesión inválida. Inicia sesión nuevamente.", onClose: () => setShowGenericModal(false) });
        return;
      }
  
      // Send the charge
      const chargePayload = {
        internal_id: selectedMachineId, // INTERNAL ID (backend maps to terminal_id + token)
        amount: amountToCharge,         // EXACT typed value in the card input
        print_on_terminal: noTicket ? "no_ticket" : "seller_ticket", // or "seller_ticket"
        default_type: "credit_card",    // debit_card | credit_card | voucher_card | cash
      };
  
      const storageKey = `ovm_terminal_attempt_${sessionData.username}`;
      const fingerprint = JSON.stringify(chargePayload);
      let pending = terminalAttemptRef.current;
      if (!pending) {
        try { pending = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { /* use memory */ }
      }
      if (pending && pending.fingerprint !== fingerprint) {
        showModal({ title: 'Cobro pendiente de verificación', message: 'Reintenta con la misma máquina y monto para consultar el cobro anterior antes de iniciar otro.', onClose: () => setShowGenericModal(false) });
        return;
      }
      if (!pending) pending = { key: createSaleOperationKey(), fingerprint };
      terminalAttemptRef.current = pending;
      try { localStorage.setItem(storageKey, JSON.stringify(pending)); } catch { /* retain in memory */ }
      const settleTerminal = async () => {
        const response = await fetch(apiUrl(`/sales/machines/charge/${encodeURIComponent(pending.key)}/acknowledge`), {
          method: 'POST', headers: { Authorization: sessionData.sessionKey, Username: sessionData.username, 'Content-Type': 'application/json' }, body: '{}',
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'No se pudo verificar el cobro anterior.');
        terminalAttemptRef.current = null;
        try { localStorage.removeItem(storageKey); } catch { /* the server receipt remains durable */ }
      };
      chargePayload.pos_snapshot = { cart, selectedLocation, cashGiven, cashAmount, workerDiscount, workerVerificationToken, clientUsername, discountedTotal };
      chargePayload.sale_operation_key = saleOperationKeyRef.current || undefined;
      chargePayload.sale_fingerprint = JSON.stringify({ cart: cart.map(item => ({ code: item.code, quantity: item.reqQty, unit_price: item.unit_price })), discount: workerDiscount || 0, location: selectedLocation });
      chargePayload.idempotency_key = pending.key;
      chargePayload.external_reference = pending.key;
      const chargeRes = await fetch(apiUrl("/sales/machines/charge"), {
        method: "POST",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(chargePayload),
      });
  
      const charge = await chargeRes.json();
      if (charge?.idempotency_key) {
        pending = { ...pending, key: charge.idempotency_key };
        terminalAttemptRef.current = pending;
        if (charge.sale_operation_key) saleOperationKeyRef.current = charge.sale_operation_key;
        try { localStorage.setItem(storageKey, JSON.stringify(pending)); } catch { /* server recovery is authoritative */ }
      }
  
      if (!chargeRes.ok || !charge?.success) {
        if (charge.retrySafe === true) {
          terminalAttemptRef.current = null;
          try { localStorage.removeItem(storageKey); } catch { /* no charge was created */ }
        }
        if (charge.requiresReconciliation) {
          showModal({ title: 'Verificar cobro anterior', message: charge.message,
            reconcile: async (orderId) => {
              const response = await fetch(apiUrl(`/sales/machines/charge/${encodeURIComponent(pending.key)}/reconcile`), {
                method: 'POST', headers: { Authorization: sessionData.sessionKey, Username: sessionData.username, 'Content-Type': 'application/json' }, body: JSON.stringify({ orderId }),
              });
              const data = await response.json();
              if (!response.ok || !data.success) throw new Error(data.message || 'No se pudo verificar la orden.');
              showModal({ title: 'Cobro recuperado', message: 'La orden coincide. Presiona Enviar cobro nuevamente para recuperar su resultado; no se creará otro cobro.', onClose: () => setShowGenericModal(false) });
            }, onClose: () => setShowGenericModal(false),
          });
          return;
        }
        showModal({
          title: "Error al enviar cobro",
          message: charge?.message || "No fue posible enviar el cobro a la máquina.",
          onClose: () => setShowGenericModal(false),
        });
        return;
      }
  
      // If MP already returned accredited (rare, but possible), treat as success immediately
      if (charge.accredited === true) {
        const alreadyApplied = appliedTerminalKeysRef.current.has(pending.key);
        const newConfirmed = (confirmedCardTotal || 0) + (alreadyApplied ? 0 : amountToCharge);
        appliedTerminalKeysRef.current.add(pending.key);

        if (newConfirmed + cashAmount >= discountedTotal) {
          // (optional) keep UI in sync
          setCardAmount(0);
          setCardGiven("");
          if (!alreadyApplied) setConfirmedCardTotal(prev => prev + amountToCharge);

          // Finish the sale using the exact amount we know right now
          setShowGenericModal(false);
          await handleSubmitSale({ cardOverride: newConfirmed });
          return;
        } else {
          // Partial immediate approval
          if (!alreadyApplied) setConfirmedCardTotal(prev => prev + amountToCharge);
          await settleTerminal();
          setCardAmount(0);
          setCardGiven("");
          showModal({
            title: "Pago aprobado",
            message: "El pago con tarjeta fue aprobado.",
            onClose: () => setShowGenericModal(false),
          });
          return;
        }
      }

  
      // Got an order id from MP — start polling
      const orderId = charge?.mp?.id;
      if (!orderId) {
        showModal({
          title: "Respuesta inválida",
          message: "No se recibió el ID de la orden desde Mercado Pago.",
          onClose: () => setShowGenericModal(false),
        });
        return;
      }
      setCurrentOrderId(orderId);
  
      // Inform user we’re waiting
      showModal({
        title: "Cobro enviado",
        message: "Esperando confirmación del pago en la máquina...",
        onClose: () => setShowGenericModal(false),
      });
  
      const outcome = await waitForPaymentCompletion(orderId, {
        intervalMs: 2500,  // poll every 2.5s
        timeoutMs: 120000, // stop after 2 minutes
      });

      if (outcome.canceled) {
        await settleTerminal();
        // Stop everything gracefully
        setCurrentOrderId(null);
        setCardAmount(0);
        setCardGiven("");
        showModal({
        title: "Pago cancelado",
        message: "La transacción fue cancelada desde la terminal.",
        onClose: () => setShowGenericModal(false),
        });
        return;
      }
  
      if (outcome.timeout) {
        showModal({
          title: "Pago pendiente",
          message: "No se confirmó a tiempo. Verifica en la máquina y vuelve a intentar.",
          onClose: () => setShowGenericModal(false),
        });
        return;
      }
      
      if (outcome.failed) {
        await settleTerminal();
        // Pull a human-friendly detail if available, e.g. “card_disabled”
        const detail = outcome?.summary?.failureReason || outcome?.reason || "Transacción fallida";
        showModal({
          title: "Pago rechazado",
          message: `Motivo: ${detail}. Intenta nuevamente o usa otro medio de pago.`,
          onClose: () => setShowGenericModal(false),
        });
        return;
      }

      if (outcome.error) {
        showModal({
          title: "Error verificando pago",
          message: outcome.error,
          onClose: () => setShowGenericModal(false),
        });
        return;
      }
      
      if (outcome.approved) {
        const approvedCard = Math.round(Number(outcome?.summary?.accreditedAmount || 0));
        const effectiveApproved = approvedCard > 0 ? approvedCard : amountToCharge;
      
        // Calculate exact confirmed total we want to store
        const alreadyApplied = appliedTerminalKeysRef.current.has(pending.key);
        const newConfirmed = (confirmedCardTotal || 0) + (alreadyApplied ? 0 : effectiveApproved);
        appliedTerminalKeysRef.current.add(pending.key);
      
        // Update UI
        if (!alreadyApplied) setConfirmedCardTotal(prev => prev + effectiveApproved);
        setCardAmount(0);
        setCardGiven("");
      
        // Recompute remaining using the *new* confirmed amount
        const remainingAfterNew = discountedTotal - (cashAmount + newConfirmed);
      
        if (remainingAfterNew <= 0) {
          showModal({
            title: "Pago aprobado",
            message: "El pago cubre el total. Registrando la venta...",
            onClose: () => setShowGenericModal(false),
          });
          // Submit immediately with the override (don't rely on async state flush)
          await handleSubmitSale({ cardOverride: newConfirmed });
          setCurrentOrderId(null);
          return;
        }
      
        await settleTerminal();
        showModal({
          title: "Pago parcial aprobado",
          message: `Se aprobaron $${effectiveApproved.toLocaleString("de-DE")}. Faltan $${remainingAfterNew.toLocaleString("de-DE")} para completar.`,
          onClose: () => setShowGenericModal(false),
        });
      }
       else {
        showModal({
          title: "Pago no aprobado",
          message: "El pago fue rechazado o cancelado.",
          onClose: () => setShowGenericModal(false),
        });
      }      
      
    } catch (e) {
      console.error("Error sending payment:", e);
      showModal({
        title: "Error de red",
        message: "No fue posible contactar a la máquina de pago.",
        onClose: () => setShowGenericModal(false),
      });
    } finally {
      terminalSendingRef.current = false;
      setSendingPayment(false);
    }
  }
  
  function summarizeOrder(mpOrder) {
    const orderStatus        = String(mpOrder?.status || '').toLowerCase();
    const orderStatusDetail  = String(mpOrder?.status_detail || '').toLowerCase();
  
    const payments = Array.isArray(mpOrder?.transactions?.payments)
      ? mpOrder.transactions.payments
      : [];
  
    const paymentStatuses      = payments.map(p => String(p?.status || '').toLowerCase());
    const paymentStatusDetails = payments.map(p => String(p?.status_detail || '').toLowerCase());
  
    // success when 'accredited'
    const accredited =
      orderStatusDetail === 'accredited' ||
      paymentStatusDetails.includes('accredited');

    
    const canceledOnTerminal =
      paymentStatusDetails.includes('canceled_on_terminal') ||
      paymentStatusDetails.includes('cancelled_on_terminal');

    const canceled =
      orderStatus === 'canceled' ||
      orderStatusDetail === 'canceled' ||
      paymentStatuses.includes('canceled') ||
      paymentStatusDetails.includes('canceled') ||
      canceledOnTerminal;
  
    // failure when 'failed'
    const failed =
      orderStatus === 'failed' ||
      orderStatusDetail === 'failed' ||
      paymentStatuses.includes('failed') ||
      paymentStatusDetails.includes('failed');
  
    // helpful reason (e.g., 'card_disabled') if present
    const failureReason =
      paymentStatusDetails.find(x => x && x !== 'approved' && x !== 'accredited') ||
      orderStatusDetail;
  
    // how much got accredited (useful in partial approvals)
    const accreditedAmount = payments
      .filter(p => String(p?.status_detail || '').toLowerCase() === 'accredited')
      .map(p => Number(p?.amount || 0))
      .reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  
    return {
      orderId: mpOrder?.id,
      orderStatus,
      orderStatusDetail,
      paymentStatuses,
      paymentStatusDetails,
      accredited,
      canceled,
      canceledOnTerminal,
      failed,
      failureReason,
      accreditedAmount,
    };
  }
  
  
  async function waitForPaymentCompletion(orderId, { intervalMs = 2500, timeoutMs = 120000 } = {}) {
    const sessionData = JSON.parse(localStorage.getItem("sessionData"));
    if (!sessionData?.sessionKey || !sessionData?.username) {
      return { done: true, approved: false, error: 'Sesión inválida' };
    }
    const start = Date.now();
  
    while (Date.now() - start < timeoutMs) {
      try {
        const res = await fetch(
          apiUrl(`/sales/machines/order-status/${encodeURIComponent(orderId)}?internal_id=${encodeURIComponent(selectedMachineId)}`),
          {
            method: "GET",
            headers: {
              Authorization: sessionData.sessionKey,
              Username: sessionData.username,
              "Content-Type": "application/json",
            },
          }
        );
  
        const data = await res.json();
        if (!res.ok) {
          return { done: true, approved: false, error: data?.message || 'Error consultando el estado' };
        }
  
        const { mp } = data;
        const summary = data?.summary ?? summarizeOrder(mp);

        if (summary.canceled) {
          return { done: true, approved: false, canceled: true, mp, summary };
        }
  
        if (summary.accredited) {
          return { done: true, approved: true, mp, summary };
        }
        if (summary.failed) {
          return { done: true, approved: false, failed: true, reason: summary.failureReason || 'failed', mp, summary };
        }
  
        await new Promise(r => setTimeout(r, intervalMs));
      } catch {
        return { done: true, approved: false, error: 'Error de red al verificar el estado' };
      }
    }
  
    return { done: false, approved: false, timeout: true };
  }
  
  
  

  // ---------- Return API ----------
  return {
    // refs
    searchContainerRef,
    locationDropdownRef,

    // state
    searchResults, setSearchResults,
    searchQuery, setSearchQuery,
    cart, setCart,
    total,
    showPaymentModal, setShowPaymentModal,
    showCartEmptyAlert, setShowCartEmptyAlert,
    workerCode, setWorkerCode,
    workerName, setWorkerName,
    workerCodeError, setWorkerCodeError,
    clientUsername,
    cashAmount, setCashAmount,
    cardAmount, setCardAmount,
    cashGiven, setCashGiven,
    cardGiven, setCardGiven,
    showGenericModal, setShowGenericModal,
    genericModalContent, setGenericModalContent,
    availableLocations,
    locationKeys,
    isSingleLocation,
    selectedLocation, setSelectedLocation,
    isLocationDropdownOpen, setIsLocationDropdownOpen,
    pendingActivation, setPendingActivation,
    activatePendingLocation,
    showLocationSelect, setShowLocationSelect,
    selectAndActivateLocation,
    pendingLocationChange, setPendingLocationChange,
    showLocationChangeModal, setShowLocationChangeModal,
    locationOptions, setLocationOptions,
    locationAvailability, setLocationAvailability,
    noAvailableLocations, setNoAvailableLocations,
    forceCloseTarget, setForceCloseTarget,
    workerDiscount, setWorkerDiscount,
    showCustomProductModal, setShowCustomProductModal,
    customProduct, setCustomProduct,
    qtyInputs, setQtyInputs,
    

    // payment machines state
    paymentMachines,
    machinesLoading,
    machinesError,
    selectedMachineId, setSelectedMachineId,
    sendingPayment,
    confirmedCardTotal,
    noTicket, setNoTicket,
    manualWithoutMachine, setManualWithoutMachine,
    canConfirm,
    submittingSale,

    // derived
    discountedTotal,
    totalEntered,
    remaining,
    posSidesWidth,


    // handlers / logic
    searchProduct,
    addProductToCart,
    removeProductFromCart,
    incrementQty,
    decrementQty,
    handleQtyChange,
    verifyWorkerCode,
    parseAmount,
    resetPaymentState,
    handleSubmitSale,
    showModal,

    // payment machines handlers
    loadPaymentMachines,
    sendCardPaymentToMachine,
  };
}
